import { useState, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { BsMegaphone, BsX } from 'react-icons/bs';
import { IconButton } from '@aibuild/ui';
import { submitFeedback } from '@api/userFeedback';
import {
  ButtonWrapper,
  FloatingButton,
  Popover,
  PopoverTitle,
  PopoverSubtitle,
  FeedbackTextarea,
  SendButton,
  ThanksMessage,
} from './FeedbackWidget.styled';

type State = 'closed' | 'open' | 'sending' | 'sent';

export interface FeedbackWidgetProps {
  /**
   * Visual mode.
   *  - `floating` (default): a circular megaphone FAB pinned to the page's
   *    bottom-right corner, with the popover floating above it.
   *  - `inline`: a quiet IconButton that fits into a topbar's action
   *    cluster, with the popover anchored just below the topbar at the
   *    page's top-right. Used by the Processes pages where the feedback
   *    affordance lives next to the theme toggle.
   */
  mode?: 'floating' | 'inline';
  /** Test id override (defaults to `feedback-widget`). */
  dataTestId?: string;
}

export default function FeedbackWidget({
  mode = 'floating',
  dataTestId = 'feedback-widget',
}: FeedbackWidgetProps = {}) {
  const intl = useIntl();
  const [widgetState, setWidgetState] = useState<State>('closed');
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isOpen = widgetState !== 'closed';

  useEffect(() => {
    if (widgetState === 'open') {
      textareaRef.current?.focus();
    }
  }, [widgetState]);

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        setWidgetState('closed');
        setText('');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  function toggleOpen() {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    if (isOpen) {
      setWidgetState('closed');
      setText('');
    } else {
      setWidgetState('open');
    }
  }

  async function handleSend() {
    const trimmed = text.trim();
    if (!trimmed || widgetState === 'sending') return;

    setWidgetState('sending');

    try {
      await submitFeedback({
        content: trimmed,
        pageUrl: globalThis.location?.href,
      });
      setWidgetState('sent');
      closeTimerRef.current = setTimeout(() => {
        setWidgetState('closed');
        setText('');
      }, 2000);
    } catch {
      // Stay open so the user can retry; don't surface internal errors
      setWidgetState('open');
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSend();
    }
  }

  const labelOpen = intl.formatMessage({
    id: 'feedbackWidget.button.open',
    defaultMessage: 'Share feedback',
  });
  const labelClose = intl.formatMessage({
    id: 'feedbackWidget.button.close',
    defaultMessage: 'Close',
  });

  const triggerLabel = isOpen ? labelClose : labelOpen;
  const triggerIcon = isOpen ? <BsX size={20} /> : <BsMegaphone size={18} />;

  return (
    <>
      {isOpen && (
        <Popover
          ref={popoverRef}
          $anchor={mode === 'inline' ? 'top-right' : 'bottom-right'}
          data-testid={`${dataTestId}__popover`}
        >
          {widgetState === 'sent' ? (
            <ThanksMessage>
              {intl.formatMessage({
                id: 'feedbackWidget.thanks',
                defaultMessage: 'Thank you — your feedback has been received.',
              })}
            </ThanksMessage>
          ) : (
            <>
              <PopoverTitle>
                {intl.formatMessage({
                  id: 'feedbackWidget.title',
                  defaultMessage: 'Share Feedback',
                })}
              </PopoverTitle>
              <PopoverSubtitle>
                {intl.formatMessage({
                  id: 'feedbackWidget.subtitle',
                  defaultMessage:
                    "What's working well? What could be better? All feedback is reviewed by the team.",
                })}
              </PopoverSubtitle>
              <FeedbackTextarea
                ref={textareaRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={intl.formatMessage({
                  id: 'feedbackWidget.placeholder',
                  defaultMessage: 'Describe your experience…',
                })}
                disabled={widgetState === 'sending'}
              />
              <SendButton
                onClick={handleSend}
                disabled={!text.trim() || widgetState === 'sending'}
                $loading={widgetState === 'sending'}
              >
                {widgetState === 'sending'
                  ? intl.formatMessage({
                      id: 'feedbackWidget.button.sending',
                      defaultMessage: 'Sending…',
                    })
                  : intl.formatMessage({
                      id: 'feedbackWidget.button.send',
                      defaultMessage: 'Send Feedback',
                    })}
              </SendButton>
            </>
          )}
        </Popover>
      )}

      {mode === 'floating' ? (
        <ButtonWrapper data-testid={`${dataTestId}__wrapper`}>
          <FloatingButton
            onClick={toggleOpen}
            aria-label={triggerLabel}
            title={triggerLabel}
            data-testid={`${dataTestId}__trigger`}
          >
            {isOpen ? <BsX size={26} /> : <BsMegaphone size={23} />}
          </FloatingButton>
        </ButtonWrapper>
      ) : (
        <IconButton
          ariaLabel={triggerLabel}
          icon={triggerIcon}
          onClick={toggleOpen}
          isOpen={isOpen}
          dataTestId={`${dataTestId}__trigger`}
        />
      )}
    </>
  );
}
